// TODO(atoms): This component needs the following atoms from @visioni/a3s-ui:
//   - ExpandableRow — pressable row with chevron toggle and collapsible content
//   - TableRow — flex row with consistent column sizing and border treatment
//   - LinkText — pressable text that navigates (replaces inline Pressable wrappers)
// See: docs/plans/a3s-activity-atoms.md

import { type FC, type ReactNode, useState } from "react";
import { Pressable, useWindowDimensions } from "react-native";
import { format } from "date-fns";
import { useUnistyles } from "react-native-unistyles";
import { View, Text, Icon, Chip } from "@visioni/a3s-ui/components";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ActivityItemViewModel {
  readonly id: string;
  readonly summary: string;
  readonly createdAt: string;
  readonly cameraKey: string | null;
  readonly cameraLabel: string | null;
  readonly userEmail: string | null;
  readonly kind: string;
  /** Whether this item can expand to show detail content. */
  readonly hasDetails: boolean;
  /** Raw metadata payload for expandable config items. */
  readonly data: Record<string, unknown> | null;
}

export interface ActivityItemCardProps {
  readonly item: ActivityItemViewModel;
  readonly isLast?: boolean;
  /** Called when the summary text is pressed (e.g. to navigate). */
  readonly onSummaryPress?: (item: ActivityItemViewModel) => void;
  /** Called when the camera label is pressed (e.g. to navigate to camera). */
  readonly onCameraPress?: (item: ActivityItemViewModel) => void;
  /** Render function for expandable detail content. */
  readonly renderDetails?: (item: ActivityItemViewModel) => ReactNode;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimestampParts(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  return { date: format(d, "d MMM yy"), time: format(d, "h:mma") };
}

// ---------------------------------------------------------------------------
// ActivityItemCard — molecule
// A single row in the activity feed with optional expandable details.
// Navigation is handled via callbacks; detail content via render prop.
// ---------------------------------------------------------------------------

export const ActivityItemCard: FC<ActivityItemCardProps> = ({
  item,
  isLast = false,
  onSummaryPress,
  onCameraPress,
  renderDetails,
}) => {
  const { theme } = useUnistyles();
  const { width } = useWindowDimensions();
  const isCompact = width < 800;
  const [showDetails, setShowDetails] = useState(false);
  const hasExpandableDetails = item.hasDetails && renderDetails != null;
  const timestamp = formatTimestampParts(item.createdAt);

  const toggleDetails = () => setShowDetails((prev) => !prev);
  const handleRowPress = () => hasExpandableDetails && toggleDetails();

  return (
    <View>
      <Pressable disabled={!hasExpandableDetails} onPress={handleRowPress}>
        <View
          flexDirection={isCompact ? "column" : "row"}
          alignItems={isCompact ? "stretch" : "center"}
          px="4xl"
          py="lg"
          borderBottomWidth={
            showDetails ? 0
            : !isLast ?
              1
            : 0
          }
          borderColor="borderCard"
          bg="surface"
          {...(isCompact ? { gap: "sm" as const } : {})}
        >
          {/* Top row on mobile: summary + chevron */}
          {isCompact ?
            <View flexDirection="row" alignItems="center">
              <View flex={1} style={{ minWidth: 0 }}>
                {onSummaryPress ?
                  <Pressable onPress={() => onSummaryPress(item)}>
                    <Text textStyle="xs/medium">{item.summary}</Text>
                  </Pressable>
                : <Text textStyle="xs/medium">{item.summary}</Text>}
              </View>
              <View width={24} alignItems="flex-end">
                {hasExpandableDetails ?
                  <Pressable
                    onPress={(event) => {
                      event.stopPropagation?.();
                      toggleDetails();
                    }}
                    hitSlop={8}
                  >
                    <Icon name={showDetails ? "chevronUp" : "chevronDown"} size={24} color={theme.colors.text} />
                  </Pressable>
                : null}
              </View>
            </View>
          : <View flex={3} pr="md" style={{ minWidth: 0 }}>
              {onSummaryPress ?
                <Pressable onPress={() => onSummaryPress(item)}>
                  <Text textStyle="xs/medium">{item.summary}</Text>
                </Pressable>
              : <Text textStyle="xs/medium">{item.summary}</Text>}
            </View>
          }

          {/* Bottom row on mobile: camera, author + time */}
          {isCompact ?
            <View gap="xs">
              {item.cameraLabel ?
                <View flexDirection="row">
                  {onCameraPress && item.cameraKey ?
                    <Chip label={item.cameraLabel} onPress={() => onCameraPress(item)} trailingIcon="arrowRight" />
                  : <Chip label={item.cameraLabel} />}
                </View>
              : null}
              <Text textStyle="xxs/regular" color="textMuted" numberOfLines={1}>
                {item.userEmail ?? "—"} · {timestamp.date} {timestamp.time}
              </Text>
            </View>
          : <>
              {/* Scope column */}
              <View flex={2} pr="md" style={{ minWidth: 0 }}>
                {item.cameraLabel ?
                  onCameraPress && item.cameraKey ?
                    <Chip label={item.cameraLabel} onPress={() => onCameraPress(item)} trailingIcon="arrowRight" />
                  : <Chip label={item.cameraLabel} />
                : <Text textStyle="xxs/regular" color="textMuted">
                    —
                  </Text>
                }
              </View>

              {/* User column */}
              <View flex={2} pr="md" style={{ minWidth: 0 }}>
                <Text textStyle="xxs/regular" color="textMuted" numberOfLines={1}>
                  {item.userEmail ?? "—"}
                </Text>
              </View>

              {/* Time column */}
              <View flex={0.5} pr="md" style={{ minWidth: 0, alignItems: "flex-end" }}>
                <Text textStyle="xxs/regular" color="textMuted" numberOfLines={1} style={{ textAlign: "right" }}>
                  {timestamp.date}
                </Text>
                <Text textStyle="xxs/regular" color="textMuted" numberOfLines={1} style={{ textAlign: "right" }}>
                  {timestamp.time}
                </Text>
              </View>

              {/* Expand chevron */}
              <View width={24} alignItems="flex-end">
                {hasExpandableDetails ?
                  <Pressable
                    onPress={(event) => {
                      event.stopPropagation?.();
                      toggleDetails();
                    }}
                    hitSlop={8}
                  >
                    <Icon name={showDetails ? "chevronUp" : "chevronDown"} size={24} color={theme.colors.text} />
                  </Pressable>
                : null}
              </View>
            </>
          }
        </View>
      </Pressable>

      {showDetails && hasExpandableDetails ?
        <View px="4xl" py="xl" bg="surface" borderBottomWidth={isLast ? 0 : 1} borderColor="borderCard">
          <View gap="lg">{renderDetails(item)}</View>
        </View>
      : null}
    </View>
  );
};
